'use client'

import ConfirmModal from '@/components/confirm-modal'
import { getRoles } from '@/lib/api/roles/roles-api'
import { getPlans, Plan } from '@/lib/api/plans/plan-api'
import {
    createSubscription,
    getSubscriptionByCompany,
} from '@/lib/api/master/master-subscription-api'
import {
    activateUserAccount,
    blockUser,
    deactivateUserAccount,
    deleteUser,
    getUsers,
    resetUserPassword,
    unblockUser,
} from '@/lib/api/users/users-api'
import { apiRequest } from '@/lib/api/request'
import { Role } from '@/lib/api/common/interfaces/role.interface'
import { User } from '@/lib/api/common/interfaces/user.interface'
import { ApiResponse } from '@/lib/api/request'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useAppSelector } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdAdd,
    MdBlock,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdGppGood,
    MdLock,
    MdPeopleAlt,
    MdRefresh,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const USER_TYPES = [
    'MASTER',
    'COMPANY',
    'CUSTOM',
    'EMPLOYEE',
    'PROFESSIONAL',
    'PARTNER',
    'CLIENT',
    'AI',
] as const
type UserType = (typeof USER_TYPES)[number]

const USER_TYPE_LABEL: Record<UserType, string> = {
    MASTER: 'Master (administrador da plataforma)',
    COMPANY: 'Company (admin de empresa)',
    CUSTOM: 'Personalizado (acesso definido pelo perfil)',
    EMPLOYEE: 'Employee (funcionário)',
    PROFESSIONAL: 'Profissional',
    PARTNER: 'Parceiro',
    CLIENT: 'Cliente',
    AI: 'Usuário de IA',
}

const USER_TYPE_BADGE: Record<string, string> = {
    MASTER: 'badge-error',
    COMPANY: 'badge-secondary',
    CUSTOM: 'badge-secondary',
    EMPLOYEE: 'badge-info',
    PROFESSIONAL: 'badge-success',
    PARTNER: 'badge-warning',
    CLIENT: 'badge-ghost',
    AI: 'badge-accent',
}

interface UserForm {
    name: string
    email: string
    password: string
    userType: UserType
    roleId: number | null
}

export default function UsersMasterPage() {
    const masterUser = useAppSelector(s => s.auth.user)
    const masterCompanyId =
        (masterUser as any)?.companyId ??
        (masterUser as any)?.company?.id ??
        null

    const [roles, setRoles] = useState<Role[]>([])
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState<UserType | ''>('')
    const [urlPage, setUrlPage] = useUrlPage()
    const debouncedSearch = useDebounce(search, 300)

    const filters = useMemo(
        () => ({
            search: debouncedSearch.trim() || undefined,
            userType: typeFilter || undefined,
        }),
        [debouncedSearch, typeFilter]
    )

    // Lista todos os usuários da plataforma (o backend não escopa por company
    // quando o ator é MASTER). Paginação no padrão do sistema.
    const fetchUsers = useCallback(
        (params: typeof filters & { page: number; limit: number }) =>
            getUsers(params as any) as unknown as Promise<ApiResponse<User[]>>,
        []
    )

    const {
        items: users,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<User, typeof filters>({
        fetchFn: fetchUsers,
        filters,
        limit: 20,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    // Role mora no vínculo UserCompany — guardamos o companyId do vínculo e a
    // role original pra atualizar via endpoint dedicado quando o MASTER troca.
    const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null)
    const [editingOriginalRoleId, setEditingOriginalRoleId] = useState<
        number | null
    >(null)
    const [submitting, setSubmitting] = useState(false)

    const [form, setForm] = useState<UserForm>({
        name: '',
        email: '',
        password: '',
        userType: 'COMPANY',
        roleId: null,
    })

    const [resetTarget, setResetTarget] = useState<User | null>(null)
    const [resetPassword, setResetPassword] = useState('')

    const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
    const [blockTarget, setBlockTarget] = useState<User | null>(null)
    const [blockReason, setBlockReason] = useState('')
    const [processing, setProcessing] = useState(false)

    // Subscription da Org do MASTER — necessaria pra limite de usuarios
    const [hasSubscription, setHasSubscription] = useState<boolean | null>(null)
    const [plans, setPlans] = useState<Plan[]>([])
    const [planModalOpen, setPlanModalOpen] = useState(false)
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null)
    const [assigningPlan, setAssigningPlan] = useState(false)

    useEffect(() => {
        loadStatic()
    }, [])

    const loadStatic = async () => {
        // Companies nao precisa carregar — todo user criado pelo master e
        // vinculado a propria company do master (auto via masterCompanyId).
        const rolesRes = await getRoles()
        if (rolesRes.success && rolesRes.result) setRoles(rolesRes.result)
        await checkSubscription()
    }

    const checkSubscription = async () => {
        if (!masterCompanyId) return
        const res = await getSubscriptionByCompany(masterCompanyId)
        setHasSubscription(!!(res.success && res.result?.id))
    }

    const openPlanModal = async () => {
        if (plans.length === 0) {
            // MASTER vê planos privados também (getPlans(false) só traz publicos)
            const res = await getPlans(true)
            if (res.success && res.result) {
                setPlans(res.result)
                if (res.result.length > 0) setSelectedPlanId(res.result[0].id)
            }
        }
        setPlanModalOpen(true)
    }

    const handleAssignPlan = async () => {
        if (!masterCompanyId) {
            toast.error('Master sem company vinculada')
            return
        }
        if (!selectedPlanId) {
            toast.warning('Selecione um plano')
            return
        }
        const orgId = (masterUser as any)?.organizationId
            ?? (masterUser as any)?.company?.organizationId
        if (!orgId) {
            toast.error('Master sem organização vinculada')
            return
        }
        setAssigningPlan(true)
        try {
            const res = await createSubscription({
                organizationId: Number(orgId),
                planId: selectedPlanId,
                dueDay: 1,
            })
            if (res.success) {
                toast.success('Plano atribuído à sua company com sucesso')
                setPlanModalOpen(false)
                await checkSubscription()
            } else {
                toast.error(res.error || 'Erro ao atribuir plano')
            }
        } finally {
            setAssigningPlan(false)
        }
    }

    const resolveRoleForType = (userType: UserType): number | null => {
        const systemName =
            userType === 'MASTER'
                ? 'master'
                : userType === 'COMPANY'
                  ? 'company'
                  : null
        if (!systemName) return null
        const role = roles.find(r => r.name?.toLowerCase() === systemName)
        return role?.id ?? null
    }

    const openCreateModal = () => {
        setEditingId(null)
        setEditingCompanyId(null)
        setEditingOriginalRoleId(null)
        setForm({
            name: '',
            email: '',
            password: '',
            userType: 'COMPANY',
            roleId: resolveRoleForType('COMPANY'),
        })
        modalRef.current?.showModal()
    }

    const openEditModal = (user: User) => {
        setEditingId(user.id)
        const userCompany = (user as any).userCompanies?.[0]
        setEditingCompanyId(userCompany?.companyId ?? null)
        setEditingOriginalRoleId(userCompany?.roleId ?? null)
        setForm({
            name: user.name ?? '',
            email: user.email ?? '',
            password: '',
            userType: ((user as any).userType ?? 'EMPLOYEE') as UserType,
            roleId: userCompany?.roleId ?? null,
        })
        modalRef.current?.showModal()
    }

    const handleSave = async () => {
        if (!form.name || !form.email) {
            toast.warning('Preencha nome e email')
            return
        }
        if (!editingId && !form.password) {
            toast.warning('Senha é obrigatória ao criar usuário')
            return
        }
        if (!masterCompanyId) {
            toast.error(
                'MASTER sem company vinculada — não é possível criar usuário'
            )
            return
        }
        // Backend (createByMaster) so aceita roleId quando e custom da org.
        // Se selecionar system role no dropdown ('master'/'company'), omite
        // o roleId e deixa o backend resolver via findBySystemName(userType).
        const selectedRole = form.roleId
            ? roles.find(r => r.id === form.roleId)
            : null
        const isSystemRoleSelected = selectedRole?.isSystem === true
        const isSystemType = form.userType === 'MASTER' || form.userType === 'COMPANY'

        // Validacao: pra non-system types, exige role custom selecionada
        if (!isSystemType && (!selectedRole || isSystemRoleSelected)) {
            toast.warning(
                `Selecione uma role customizada pra userType ${form.userType}`,
            )
            return
        }
        // Pra MASTER/COMPANY, role custom ok ou system role ok (omite roleId)
        const finalRoleId = isSystemRoleSelected || !selectedRole
            ? undefined
            : selectedRole.id

        setSubmitting(true)
        try {
            if (editingId) {
                // PATCH /users/edit/:id (UpdateUserByMasterDto) — atualiza só
                // dados básicos; NÃO aceita role (a role mora no vínculo).
                const res = await apiRequest<any>(
                    `users/edit/${editingId}`,
                    {
                        data: {
                            name: form.name,
                            email: form.email,
                            userType: form.userType,
                        },
                    },
                    'patch'
                )
                if (!res.success) {
                    toast.error(res.error || 'Erro ao atualizar')
                    return
                }
                // Role vive no UserCompany — atualiza via endpoint dedicado
                // quando o MASTER trocou a seleção do dropdown.
                if (
                    form.roleId &&
                    editingCompanyId &&
                    form.roleId !== editingOriginalRoleId
                ) {
                    const roleRes = await apiRequest<any>(
                        `user-company/${editingId}/${editingCompanyId}`,
                        { data: { roleId: form.roleId } },
                        'patch'
                    )
                    if (!roleRes.success) {
                        toast.error(
                            roleRes.error ||
                                'Usuário salvo, mas a role não pôde ser atualizada'
                        )
                        modalRef.current?.close()
                        reload()
                        return
                    }
                }
                toast.success('Usuário atualizado')
                modalRef.current?.close()
                reload()
            } else {
                // POST /users/add-new (CreateUserByMasterDto) — companyId+roleId direto.
                const res = await apiRequest<any>(
                    'users/add-new',
                    {
                        data: {
                            name: form.name,
                            email: form.email,
                            password: form.password,
                            userType: form.userType,
                            companyId: masterCompanyId,
                            ...(finalRoleId ? { roleId: finalRoleId } : {}),
                        },
                    },
                    'post'
                )
                if (res.success) {
                    toast.success('Usuário criado')
                    modalRef.current?.close()
                    reload()
                } else {
                    toast.error(res.error || 'Erro ao criar')
                }
            }
        } finally {
            setSubmitting(false)
        }
    }

    const handleToggleActive = async (user: User) => {
        const action = (user as any).active
            ? deactivateUserAccount
            : activateUserAccount
        const res = await action(user.id)
        if (res.success) {
            toast.success(
                `Usuário ${(user as any).active ? 'desativado' : 'ativado'}`
            )
            reload()
        } else toast.error(res.error || 'Erro')
    }

    const handleResetPassword = async () => {
        if (!resetTarget || !resetPassword) return
        setProcessing(true)
        try {
            const res = await resetUserPassword(resetTarget.id, resetPassword)
            if (res.success) {
                toast.success('Senha resetada')
                setResetTarget(null)
                setResetPassword('')
            } else toast.error(res.error || 'Erro')
        } finally {
            setProcessing(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteTarget) return
        setProcessing(true)
        try {
            const res = await deleteUser(deleteTarget.id)
            if (res.success) {
                toast.success('Usuário removido')
                setDeleteTarget(null)
                reload()
            } else toast.error(res.error || 'Erro')
        } finally {
            setProcessing(false)
        }
    }

    const handleBlock = async () => {
        if (!blockTarget) return
        setProcessing(true)
        try {
            const res = await blockUser(blockTarget.id, blockReason)
            if (res.success) {
                toast.success('Usuário bloqueado')
                setBlockTarget(null)
                setBlockReason('')
                reload()
            } else toast.error(res.error || 'Erro ao bloquear')
        } finally {
            setProcessing(false)
        }
    }

    const handleUnblock = async (user: User) => {
        const res = await unblockUser(user.id)
        if (res.success) {
            toast.success('Usuário desbloqueado')
            reload()
        } else toast.error(res.error || 'Erro ao desbloquear')
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdPeopleAlt /> Usuários da plataforma
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Cria usuários MASTER (admins da plataforma), COMPANY
                        (admins de empresas) ou outros tipos. Cada user é
                        vinculado a uma Company + Role.
                    </p>
                </div>
                <button className="btn btn-secondary" onClick={openCreateModal}>
                    <MdAdd /> Novo usuário
                </button>
            </div>

            {hasSubscription === false && (
                <div className="alert alert-warning mb-4">
                    <div className="flex-1">
                        <h3 className="font-bold">Sua company não tem plano ativo</h3>
                        <p className="text-sm">
                            Sem plano, o limite de usuários é 0 e a criação é bloqueada.
                            Atribua um plano à company do MASTER pra liberar a operação.
                        </p>
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={openPlanModal}>
                        Atribuir plano
                    </button>
                </div>
            )}

            <div className="card bg-base-100 shadow mb-4">
                <div className="card-body p-4 flex flex-row gap-4">
                    <div className="form-control flex-1">
                        <label className="label">
                            <span className="label-text">
                                Buscar (nome/email)
                            </span>
                        </label>
                        <input
                            className="input input-bordered input-sm"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="nome ou email..."
                        />
                    </div>
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Tipo</span>
                        </label>
                        <select
                            className="select select-bordered select-sm"
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value as any)}
                        >
                            <option value="">Todos</option>
                            {USER_TYPES.map(t => (
                                <option key={t} value={t}>
                                    {USER_TYPE_LABEL[t]}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button className="btn btn-ghost btn-sm" onClick={reload}>
                            <MdRefresh /> Atualizar
                        </button>
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    {loading && (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                        </div>
                    )}
                    {!loading && users.length === 0 && (
                        <div className="text-center py-12 text-base-content/60">
                            Nenhum usuário encontrado.
                        </div>
                    )}
                    {!loading && users.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>Tipo</th>
                                        <th>Company</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => {
                                        const uc = (u as any).userCompanies?.[0]
                                        const userType = (u as any).userType
                                        return (
                                            <tr key={u.id}>
                                                <td className="font-medium">
                                                    {u.name}
                                                </td>
                                                <td className="text-sm">
                                                    {u.email}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge badge-sm ${USER_TYPE_BADGE[userType] ?? 'badge-ghost'}`}
                                                    >
                                                        {userType}
                                                    </span>
                                                </td>
                                                <td className="text-sm">
                                                    {uc?.company?.name ??
                                                        `#${uc?.companyId ?? '—'}`}
                                                </td>
                                                <td>
                                                    <div className="flex flex-col gap-1">
                                                        <span
                                                            className={`badge badge-sm ${(u as any).active ? 'badge-success' : 'badge-ghost'}`}
                                                        >
                                                            {(u as any).active
                                                                ? 'Ativo'
                                                                : 'Inativo'}
                                                        </span>
                                                        {(u as any).blocked && (
                                                            <span
                                                                className="badge badge-sm badge-error gap-1"
                                                                title={
                                                                    (u as any)
                                                                        .blockedReason ||
                                                                    'Bloqueado'
                                                                }
                                                            >
                                                                <MdBlock className="text-xs" />
                                                                Bloqueado
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap">
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() =>
                                                            openEditModal(u)
                                                        }
                                                    >
                                                        <MdEdit />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() =>
                                                            setResetTarget(u)
                                                        }
                                                        title="Resetar senha"
                                                    >
                                                        <MdLock />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() =>
                                                            handleToggleActive(
                                                                u
                                                            )
                                                        }
                                                        title={
                                                            (u as any).active
                                                                ? 'Desativar'
                                                                : 'Ativar'
                                                        }
                                                    >
                                                        {(u as any).active ? (
                                                            <MdClose />
                                                        ) : (
                                                            <MdCheck />
                                                        )}
                                                    </button>
                                                    {(u as any).blocked ? (
                                                        <button
                                                            className="btn btn-ghost btn-xs text-success"
                                                            onClick={() =>
                                                                handleUnblock(u)
                                                            }
                                                            title="Desbloquear"
                                                        >
                                                            <MdGppGood />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-ghost btn-xs text-warning"
                                                            onClick={() =>
                                                                setBlockTarget(u)
                                                            }
                                                            title="Bloquear (segurança)"
                                                        >
                                                            <MdBlock />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={() =>
                                                            setDeleteTarget(u)
                                                        }
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!loading && total > 0 && (
                        <div className="px-4 border-t border-base-300/60">
                            <PaginationControls
                                page={page}
                                limit={limit}
                                total={total}
                                loading={loading}
                                onPageChange={setPage}
                                label="usuário"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar usuário' : 'Novo usuário'}
                    </h3>
                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nome</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={form.name}
                                onChange={e =>
                                    setForm({ ...form, name: e.target.value })
                                }
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Email</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                value={form.email}
                                onChange={e =>
                                    setForm({ ...form, email: e.target.value })
                                }
                            />
                        </div>
                        {!editingId && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Senha</span>
                                </label>
                                <input
                                    type="password"
                                    className="input input-bordered w-full"
                                    value={form.password}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            password: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        )}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Tipo de usuário
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.userType}
                                onChange={e => {
                                    const newType = e.target.value as UserType
                                    setForm({
                                        ...form,
                                        userType: newType,
                                        roleId: resolveRoleForType(newType),
                                    })
                                }}
                            >
                                {USER_TYPES.map(t => (
                                    <option key={t} value={t}>
                                        {USER_TYPE_LABEL[t]}
                                    </option>
                                ))}
                            </select>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    (MASTER → painel master, COMPANY → painel da
                                    empresa).
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Role</span>
                                <span className="label-text-alt text-base-content/50">
                                    Auto-selecionada por tipo. Pode trocar.
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.roleId ?? ''}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        roleId: e.target.value
                                            ? Number(e.target.value)
                                            : null,
                                    })
                                }
                            >
                                <option value="">Selecione</option>
                                {roles.map(r => (
                                    <option key={r.id} value={r.id}>
                                        {r.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => modalRef.current?.close()}
                            disabled={submitting}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : editingId ? (
                                'Salvar'
                            ) : (
                                'Criar'
                            )}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            {/* Reset password */}
            {resetTarget && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            Resetar senha — {resetTarget.name}
                        </h3>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nova senha</span>
                            </label>
                            <input
                                type="password"
                                className="input input-bordered w-full"
                                value={resetPassword}
                                onChange={e => setResetPassword(e.target.value)}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setResetTarget(null)
                                    setResetPassword('')
                                }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleResetPassword}
                                disabled={processing || !resetPassword}
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Resetar'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {/* Bloquear usuário */}
            {blockTarget && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                            <MdBlock className="text-warning" /> Bloquear —{' '}
                            {blockTarget.name}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            O bloqueio impede login, derruba as sessões ativas
                            e desconecta o usuário imediatamente. Diferente de
                            desativar, é uma medida de segurança.
                        </p>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Motivo (opcional)
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                value={blockReason}
                                onChange={e => setBlockReason(e.target.value)}
                                placeholder="Ex: atividade suspeita, solicitação do titular..."
                                rows={3}
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setBlockTarget(null)
                                    setBlockReason('')
                                }}
                                disabled={processing}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-warning"
                                onClick={handleBlock}
                                disabled={processing}
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Bloquear'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {/* Delete */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remover usuário"
                message={`Tem certeza que deseja remover ${deleteTarget?.name}? Esta ação não pode ser desfeita.`}
                loading={processing}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />

            {/* Modal: atribuir plano */}
            {planModalOpen && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            Atribuir plano à sua company (MASTER)
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Necessário pra liberar limites de recursos (usuários, departamentos, etc).
                            Cria a Subscription sem cobrança real.
                        </p>
                        {plans.length === 0 ? (
                            <div className="alert alert-error text-sm">
                                Nenhum plano ativo cadastrado. Crie um plano em /master/plans antes.
                            </div>
                        ) : (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Plano</span>
                                </label>
                                <select
                                    className="select select-bordered"
                                    value={selectedPlanId ?? ''}
                                    onChange={e =>
                                        setSelectedPlanId(
                                            e.target.value ? Number(e.target.value) : null,
                                        )
                                    }
                                >
                                    {plans.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setPlanModalOpen(false)}
                                disabled={assigningPlan}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleAssignPlan}
                                disabled={assigningPlan || plans.length === 0 || !selectedPlanId}
                            >
                                {assigningPlan ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Atribuir'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    )
}
